import type { CareplanOrderLine } from "@/domains/careplan/factories/create-careplan-order-model";
import { SUBSCRIPTION_STATUS } from "@/domains/subscriptions/models/constants";
import type { SubscriptionStatus } from "@/domains/subscriptions/models/types";

export type CartLinkIntent = "view-cart" | "restore" | "none";

type CartLinkCopy = {
  prefix: string | null;
  action: string | null;
};

export type CartLinkState = CartLinkCopy & {
  intent: CartLinkIntent;
  compact: CartLinkCopy;
};

type GetCartLinkStateOptions = {
  line: CareplanOrderLine;
  subscriptionStatus: SubscriptionStatus | null;
  isProcessing: boolean;
};

export const getCartLinkState = ({
  isProcessing,
  line,
  subscriptionStatus,
}: GetCartLinkStateOptions): CartLinkState => {
  if (isProcessing) {
    return {
      prefix: "Order processing.",
      action: null,
      intent: "none",
      compact: { prefix: "Order processing.", action: null },
    };
  }

  if (line.subscriptionProduct) {
    const { quantity } = line.subscriptionProduct;

    switch (subscriptionStatus) {
      case SUBSCRIPTION_STATUS.ACTIVE:
        return {
          prefix: quantity > 1 ? `Subscribed, ${quantity} a month.` : "Subscribed.",
          action: "Manage Subscription",
          intent: "view-cart",
          compact: { prefix: null, action: "Manage Subscription" },
        };
      case SUBSCRIPTION_STATUS.PAUSED:
        return {
          prefix: "Subscription paused.",
          action: "Resume",
          intent: "view-cart",
          compact: { prefix: null, action: "Resume Subscription" },
        };
      case SUBSCRIPTION_STATUS.PENDING:
      case SUBSCRIPTION_STATUS.HAS_RECOMMENDATIONS:
        return {
          prefix: "Subscription update pending.",
          action: "View Cart",
          intent: "view-cart",
          compact: { prefix: "Update pending.", action: "View Cart" },
        };
      case SUBSCRIPTION_STATUS.CANCELLED:
      case SUBSCRIPTION_STATUS.EXPIRED:
      case SUBSCRIPTION_STATUS.INACTIVE:
        return {
          prefix: "Subscription ended.",
          action: "Reactivate",
          intent: "view-cart",
          compact: { prefix: null, action: "Reactivate Subscription" },
        };
      default:
        return {
          prefix: "Subscribed.",
          action: "View Cart",
          intent: "view-cart",
          compact: { prefix: "Subscribed.", action: "View Cart" },
        };
    }
  }

  if (!line.isActive) {
    return {
      prefix: "Not in your order.",
      action: "Add to Cart",
      intent: "restore",
      compact: { prefix: "Removed.", action: "Add to Cart" },
    };
  }

  const hasSubscription =
    subscriptionStatus !== null && subscriptionStatus !== SUBSCRIPTION_STATUS.NO_SUBSCRIPTION;

  if (hasSubscription) {
    return {
      prefix: "New to your plan.",
      action: "Update Subscription",
      intent: "view-cart",
      compact: { prefix: null, action: "Update Subscription" },
    };
  }

  return {
    prefix: null,
    action: "Remove or Edit Quantity",
    intent: "view-cart",
    compact: { prefix: null, action: "Remove or Edit Quantity" },
  };
};
